import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import solrProductImage from "@/assets/solr-focus-outdoor.jpg";

const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const scrollToProducts = () => {
    const shopSection = document.getElementById('shop-section');
    if (shopSection) {
      shopSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-[calc(100vh-80px)] sm:min-h-screen flex items-center pt-4 sm:pt-6 md:pt-8 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 md:px-6 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 items-center">
          <div className={`space-y-4 sm:space-y-5 md:space-y-6 text-center lg:text-left transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-solr-electric-blue font-medium text-xs sm:text-sm md:text-base animate-pulse">NEURAL ENHANCED BEVERAGES</span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-solr-starfield-white">
              Elevate Your Mind With 
              <span className="neural-text block mt-1">Cosmic Clarity</span>
            </h1>
            <p className="text-base sm:text-lg text-solr-starfield-silver max-w-xl mx-auto lg:mx-0">
              Experience the perfect fusion of cosmic energy and neural enhancement with our premium nootropic drinks. Designed for sustainable mental clarity, focus and cognitive performance.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4 justify-center lg:justify-start">
              <Link to="/shop">
                <Button className="btn-primary w-full sm:w-auto group relative overflow-hidden">
                  <span className="relative z-10 flex items-center">
                    Shop Collection
                    <ChevronDown className="w-4 h-4 ml-2 group-hover:translate-y-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-solr-nebula-purple to-solr-electric-blue opacity-0 group-hover:opacity-100 transition-opacity" />
                </Button>
              </Link>
              <Link to="/about">
                <Button className="btn-secondary w-full sm:w-auto">Learn More</Button>
              </Link>
            </div>
          </div>

          <div className={`relative order-first lg:order-last transition-all duration-700 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Hero Product Display */}
            <div className="relative flex items-center justify-center">
              {/* Glow effects behind image */}
              <div className="absolute inset-0 bg-gradient-radial from-solr-nebula-purple/30 via-transparent to-transparent blur-3xl scale-150" />
              
              {/* Product Image with mask blend */}
              <div className="relative z-10 animate-float">
                <img 
                  src={solrProductImage}
                  alt="SOLR premium nootropic energy drink"
                  className="w-full max-w-[280px] sm:max-w-[350px] md:max-w-[400px] lg:max-w-[500px] h-auto rounded-2xl object-cover shadow-2xl"
                  loading="eager"
                  style={{
                    maskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)',
                  }}
                />
                {/* Purple glow overlay */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-solr-nebula-purple/40 via-transparent to-transparent pointer-events-none" />
              </div>
              
              {/* Floating badges */}
              <div className="absolute top-4 right-0 sm:top-8 sm:-right-4 bg-gradient-to-r from-solr-synapse-gold/90 to-solr-synapse-gold text-solr-space-darker backdrop-blur-sm rounded-full px-3 py-1.5 sm:px-4 sm:py-2 animate-float shadow-lg z-20">
                <div className="text-xs sm:text-sm font-bold flex items-center gap-1">
                  <span>⚡</span> Best Seller
                </div>
              </div>
              <div className="absolute bottom-20 left-0 sm:bottom-24 sm:-left-4 bg-gradient-to-r from-solr-nebula-purple/90 to-solr-electric-blue/90 text-white backdrop-blur-sm rounded-full px-3 py-1.5 sm:px-4 sm:py-2 animate-float shadow-lg z-20" style={{ animationDelay: '1s' }}>
                <div className="text-xs sm:text-sm font-bold flex items-center gap-1">
                  <span>🧠</span> Nootropic Blend
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button 
        onClick={scrollToProducts}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-solr-starfield-silver hover:text-solr-nebula-purple transition-colors cursor-pointer animate-bounce"
        aria-label="Scroll to products"
      >
        <span className="text-xs uppercase tracking-widest">Explore</span>
        <ChevronDown className="w-6 h-6" />
      </button>
    </section>
  );
};

export default HeroSection;
